import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const Footer = () => {
  const [email, setEmail] = React.useState('');
  const [subscribing, setSubscribing] = React.useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-primary text-primary-foreground">


      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <img
                src="/logosrisuktam.jpg"
                alt="SriSuktam Organics"
                className="h-16 w-auto mb-4 bg-white/10 rounded-lg p-2"
              />
              <h3 className="font-serif text-xl font-semibold mb-2">SriSuktam Organics</h3>
              <p className="text-primary-foreground/80 text-sm italic mb-4">Swasthya Shuddi..</p>
              <p className="text-primary-foreground/70 text-sm leading-relaxed">
                Bringing the purity of traditional Indian organic farming to modern homes.
                Sourced directly from local farmers.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/products" className="text-primary-foreground/70 hover:text-white transition-colors">
                    All Products
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-primary-foreground/70 hover:text-white transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Blog & Recipes
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Categories</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/products?category=cat-oils" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Cold Pressed Oils
                  </Link>
                </li>
                <li>
                  <Link to="/products?category=cat-millets" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Millets
                  </Link>
                </li>
                <li>
                  <Link to="/products?category=cat-ghee" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Desi Ghee
                  </Link>
                </li>
                <li>
                  <Link to="/products?category=cat-spices" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Spices
                  </Link>
                </li>
                <li>
                  <Link to="/products?category=cat-rice" className="text-primary-foreground/70 hover:text-white transition-colors">
                    Organic Rice
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-lg mb-4">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 mt-0.5 text-accent" />
                  <span className="text-primary-foreground/70 text-sm">
                    No-109, Gavipuram Housing Society,<br />
                    Nagadevanahalli, Bangalore – 560056<br />
                    Landmark: Opposite to Nage Mane, Amma Ashrama Road
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-accent" />
                  <a href="tel:+916360657668" className="text-primary-foreground/70 hover:text-white transition-colors">
                    +91 63606 57668
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-accent" />
                  <a href="mailto:info@srisuktam.com" className="text-primary-foreground/70 hover:text-white transition-colors">
                    info@srisuktam.com
                  </a>
                </li>
              </ul>

              {/* Social Links */}
              <div className="flex gap-4 mt-6">
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Twitter className="h-5 w-5" />
                </a>
                <a href="#" className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                  <Youtube className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-primary-foreground/60 text-sm">
              © 2026 SriSuktam Organics. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy-policy" className="text-primary-foreground/60 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/shipping-policy" className="text-primary-foreground/60 hover:text-white transition-colors">
                Shipping Policy
              </Link>
              <Link to="/refund-policy" className="text-primary-foreground/60 hover:text-white transition-colors">
                Refund Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
